// Zooming into a picture or a video in the preview: the wheel, a pinch on a trackpad or a screen,
// or a double click where it is not already taken zoom in around the point under them, and a zoomed picture is dragged to look
// around it. Buttons and keys zoom around the middle of what is on screen.
//
// The thing zoomed sits on a surface that fills a parent which clips it. The surface is scaled and
// moved with a transform, never laid out again, so the picture underneath never reflows, and the
// point under the pointer stays under it as the scale changes. At its fitted scale it carries no
// transform at all, and looks exactly as it would with no zooming.
//
// What can be dragged into view is the content, not the surface: the letterboxing around a picture
// fitted into a stage of a different shape is not something to be looked at. Each use says where
// its content sits on the surface through "contentBox". A content wider than the stage can be
// dragged until its edges meet the stage's, and one narrower than it sits in the middle.
//
// Pinching is read three ways, since browsers report it three ways: Chrome and Firefox send a
// trackpad pinch as a wheel turned with Ctrl held, Safari sends gesture events of its own, and a
// touch screen sends two pointers. The wheel is taken whole, held or not, since nothing in a
// fitted preview scrolls. Safari on a touch screen sends its gesture events alongside the two
// pointers, so they are only read when no pointer is down, which leaves them to the trackpad.
//
// A drag or a pinch moves faster than the page can render: a phone reports a finger a hundred
// times a second. While one lasts, the surface's transform is written straight onto it once a
// frame, the sizes it is held within are measured once as it starts, and the view is handed back
// to the page only when the last pointer lifts. The surface wears "panning" meanwhile, which
// keeps it on a layer of its own; dropping that layer afterwards has the browser draw the picture
// again at its new scale, sharp.
//
// A drag ends with the browser's click like any other press, which would play or pause a video
// beneath it. A click that ends a drag is stopped at the surface, so only a press that stayed put
// reaches what is under it.
import {useEffect, useRef, useState} from 'react'

// A view is how far the surface is moved, in pixels, and how much it is scaled.
export const FITTED = {scale: 1, left: 0, top: 0}

const STEP = 1.5
const DOUBLE_CLICK_SCALE = 2
// How far the pointer can wander, in pixels, before a press is taken for a drag.
const SLOP = 4
// A notch of a mouse wheel is about a hundred pixels; a trackpad pinch reports far smaller ones.
const WHEEL_RATE = 0.002
const PINCH_RATE = 0.01
const LINE_PIXELS = 16

const clamp = (value, low, high) => Math.min(high, Math.max(low, value))

const clampAxis = (offset, scale, stage, start, length) => {
  const span = scale * length

  if (span <= stage) return (stage - span) / 2 - scale * start

  return clamp(offset, stage - scale * (start + length), -scale * start)
}

/**
 * Keeps a zoomed content covering the stage wherever it is larger than it, and centred on it
 * wherever it is not. "bounds" is the stage's size and the content's box on the surface.
 */
export const clampPan = ({scale, left, top}, {width, height, content}) => {
  if (scale <= 1) return FITTED

  return {
    scale,
    left: clampAxis(left, scale, width, content.left, content.width),
    top: clampAxis(top, scale, height, content.top, content.height)
  }
}

/** Changes the scale, within 1 and "most", keeping what is under "point" there. */
export const zoomAt = (view, scale, point, bounds, most) => {
  const next = clamp(scale, 1, most)
  const ratio = next / view.scale

  return clampPan({
    scale: next,
    left: point.left - (point.left - view.left) * ratio,
    top: point.top - (point.top - view.top) * ratio
  }, bounds)
}

export const panBy = (view, across, down, bounds) => clampPan({...view, left: view.left + across, top: view.top + down}, bounds)

export const formatZoom = scale => `${Math.round(scale * 100)}%`

const wholeSurface = surface => ({left: 0, top: 0, width: surface.offsetWidth, height: surface.offsetHeight})

const clientPoint = event => ({left: event.clientX, top: event.clientY})

const styleOf = ({scale, left, top}) => (scale > 1 ? {transform: `translate(${left}px, ${top}px) scale(${scale})`, transformOrigin: '0 0'} : null)

const spread = pointers => {
  const [first, second] = [...pointers.values()]

  return {
    distance: Math.hypot(first.left - second.left, first.top - second.top),
    middle: {left: (first.left + second.left) / 2, top: (first.top + second.top) / 2}
  }
}

/**
 * The zoom of one surface: a ref for it, the style and handlers it wears, and the controls that
 * zoom it. "ready" is whether the surface is on screen, so controls are only offered for something
 * that can be zoomed. A new surface starts fitted.
 */
const useZoom = ({most, contentBox = wholeSurface, doubleClick = true}) => {
  const [surface, setSurface] = useState(null)
  const [view, setView] = useState(FITTED)
  const [panning, setPanning] = useState(false)
  // Events can arrive faster than renders, so each one builds on the last, not on what was drawn.
  const latest = useRef(FITTED)
  const pointers = useRef(new Map())
  const travelled = useRef(0)
  const pinchedFrom = useRef(1)
  // What a drag or a pinch is held within, measured as its first pointer lands.
  const gesture = useRef(null)
  const frame = useRef(0)

  const show = next => {
    latest.current = next
    setView(next)
  }

  const paint = () => {
    const style = styleOf(latest.current)

    frame.current = 0
    surface.style.transform = style?.transform ?? ''
    surface.style.transformOrigin = style?.transformOrigin ?? ''
  }

  // The view in state is left as it was until "settle", so a render meanwhile hands the surface the
  // same style as before, which React does not write again over this.
  const glide = next => {
    latest.current = next
    if (!frame.current) frame.current = requestAnimationFrame(paint)
  }

  const settle = () => {
    cancelAnimationFrame(frame.current)
    paint()
    setView(latest.current)
  }

  useEffect(() => () => cancelAnimationFrame(frame.current), [])

  const measure = () => ({width: surface.offsetWidth, height: surface.offsetHeight, content: contentBox(surface)})

  const measureStage = () => surface.parentElement.getBoundingClientRect()

  const pointIn = ({left, top}, stage = measureStage()) => ({left: left - stage.left, top: top - stage.top})

  const middle = () => ({left: surface.offsetWidth / 2, top: surface.offsetHeight / 2})

  const scaleTo = (scale, point = middle()) => surface && show(zoomAt(latest.current, scale, point, measure(), most))

  const reset = () => show(FITTED)

  useEffect(reset, [surface])

  useEffect(() => {
    if (!surface) return

    const wheel = event => {
      event.preventDefault()

      const pixels = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? event.deltaY * LINE_PIXELS : event.deltaY
      const rate = event.ctrlKey ? PINCH_RATE : WHEEL_RATE

      scaleTo(latest.current.scale * Math.exp(-pixels * rate), pointIn(clientPoint(event)))
    }

    const gestureStart = event => {
      event.preventDefault()
      pinchedFrom.current = latest.current.scale
    }

    const gestureChange = event => {
      event.preventDefault()
      if (!pointers.current.size) scaleTo(pinchedFrom.current * event.scale, pointIn(clientPoint(event)))
    }

    const refit = new ResizeObserver(() => show(clampPan(latest.current, measure())))

    surface.addEventListener('wheel', wheel, {passive: false})
    surface.addEventListener('gesturestart', gestureStart)
    surface.addEventListener('gesturechange', gestureChange)
    refit.observe(surface)

    return () => {
      surface.removeEventListener('wheel', wheel)
      surface.removeEventListener('gesturestart', gestureStart)
      surface.removeEventListener('gesturechange', gestureChange)
      refit.disconnect()
    }
  }, [surface])

  const track = event => pointers.current.set(event.pointerId, clientPoint(event))

  const release = event => {
    pointers.current.delete(event.pointerId)
    if (pointers.current.size) return

    setPanning(false)
    if (!gesture.current) return

    gesture.current = null
    settle()
  }

  // Each moves the view after the pointers and says how far that went, to tell a drag from a press.
  const pinch = event => {
    const before = spread(pointers.current)

    track(event)

    const after = spread(pointers.current)
    const across = after.middle.left - before.middle.left
    const down = after.middle.top - before.middle.top
    const {bounds, stage} = gesture.current
    const pinched = zoomAt(latest.current, latest.current.scale * (after.distance / before.distance), pointIn(after.middle, stage), bounds, most)

    glide(panBy(pinched, across, down, bounds))

    return Math.abs(after.distance - before.distance) + Math.hypot(across, down)
  }

  const drag = (event, last) => {
    track(event)

    if (latest.current.scale <= 1) return 0

    const across = event.clientX - last.left
    const down = event.clientY - last.top

    glide(panBy(latest.current, across, down, gesture.current.bounds))

    return Math.hypot(across, down)
  }

  const zoomOnDoubleClick = event => (latest.current.scale > 1 ? reset() : scaleTo(DOUBLE_CLICK_SCALE, pointIn(clientPoint(event))))

  const handlers = {
    onPointerDown: event => {
      if (event.pointerType === 'mouse' && event.button !== 0) return
      if (!pointers.current.size) {
        travelled.current = 0
        gesture.current = {bounds: measure(), stage: measureStage()}
      }

      track(event)
    },
    onPointerMove: event => {
      const last = pointers.current.get(event.pointerId)

      if (!last) return

      travelled.current += pointers.current.size === 2 ? pinch(event) : drag(event, last)

      if (travelled.current > SLOP && !panning) {
        surface.setPointerCapture?.(event.pointerId)
        setPanning(true)
      }
    },
    onPointerUp: release,
    onPointerCancel: release,
    // A touch is captured by the picture it lands on, so taking it to the surface makes the picture
    // lose it, and that loss bubbles up here: only the surface's own loss ends the press.
    onLostPointerCapture: event => event.target === surface && release(event),
    // A pointer that wanders off before it counts as a drag was never captured, so its release
    // would land elsewhere.
    onPointerLeave: event => surface.hasPointerCapture?.(event.pointerId) || release(event),
    onClickCapture: event => {
      if (travelled.current <= SLOP) return

      event.stopPropagation()
      event.preventDefault()
    },
    ...(doubleClick && {onDoubleClick: zoomOnDoubleClick})
  }

  const zoomed = view.scale > 1

  return {
    ref: setSurface,
    ready: surface !== null,
    scale: view.scale,
    zoomed,
    largest: view.scale >= most,
    panning,
    style: styleOf(view),
    handlers,
    zoomIn: () => scaleTo(latest.current.scale * STEP),
    zoomOut: () => scaleTo(latest.current.scale / STEP),
    reset
  }
}

export default useZoom
